import React, { useEffect, useRef, useState } from "react";
import SiteHeader from '../../components/SiteHeader';
import { useParams, useNavigate, Link } from "react-router-dom";
import Joi from 'joi-browser';
import { toast } from 'react-toastify';
import { FaWhatsapp, FaFacebookF, FaLinkedinIn, FaXTwitter, FaLink, FaPhone, FaRegClock, FaRegCalendar, FaArrowRight } from 'react-icons/fa6';
import "./style.scss";

import FooterArea from "../../components/FooterArea";
import Seo from "../Seo";
import FaqSection from "../FaqSection";
import { SITE_NAME, SITE_URL, absoluteUrl, absoluteAsset, breadcrumbSchema, faqSchema } from "../../seo/config";
import { slugify, blogPath, tagPath, isMongoId, toPlainText, readingTimeMinutes, formatDate } from "../../utils/blog";
import { getBlog, getBlogIndex } from "../../utils/content";
import { submitLead, CONTACT_PHONE } from "../../utils/leads";


const BlogPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blogData, setBlogData] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    country: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Joi validation schema
  const schema = {
    email: Joi.string().email({ minDomainAtoms: 2 }).required().error(errors => {
      errors.forEach(err => {
        switch (err.type) {
          case "string.email":
            err.message = 'Email must be a valid email';
            break;
          default:
            err.message = 'Email cannot be empty';
            break;
        }
      });
      return errors;
    }),
    phone: Joi.string().min(10).max(13).required().error(errors => {
      errors.forEach(err => {
        switch (err.type) {
          case "string.min":
            err.message = 'Phone number must be at least 10 characters long';
            break;
          case "string.max":
            err.message = 'Phone number cannot be longer than 13 characters';
            break;
          default:
            err.message = 'Phone cannot be empty';
            break;
        }
      });
      return errors;
    }),
    name: Joi.string().required().error(errors => {
      errors.forEach(err => {
        err.message = 'Name cannot be empty';
      });
      return errors;
    }),
    country: Joi.string().allow('').optional(),
    message: Joi.string().min(10).required().error(errors => {
      errors.forEach(err => {
        switch (err.type) {
          case "string.min":
            err.message = 'Message must be at least 10 characters long';
            break;
          default:
            err.message = 'Message cannot be empty';
            break;
        }
      });
      return errors;
    }),
  };

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const [current, all] = await Promise.all([getBlog(slug), getBlogIndex()]);

        if (!current) {
          // Non-canonical link (title-derived slug or legacy /blog/<_id>) —
          // resolve it and redirect to the canonical slug URL.
          const match = all.find((b) => slugify(b.title) === slug) || (isMongoId(slug) && all.find((b) => b._id === slug));
          if (match) {
            navigate(blogPath(match), { replace: true });
            return;
          }
        }

        setBlogData(current);
        // The index is already newest-first.
        setRelatedBlogs(current ? all.filter((b) => b.slug !== current.slug).slice(0, 3) : []);
      } catch (error) {
        console.error("Error fetching blog:", error);
        toast.error('Failed to load blog details');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, navigate]);

  // Validate individual input
  const validateProperty = (name, value) => {
    const obj = { [name]: value };
    const propertySchema = { [name]: schema[name] };
    const { error } = Joi.validate(obj, propertySchema);
    return error ? error.details[0].message : null;
  };

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errorMessage = validateProperty(name, value);
    
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));

    // Update errors
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors };
      if (errorMessage) {
        newErrors[name] = errorMessage;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  // Validate entire form
  const validateForm = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(formData, schema, options);
    
    if (!error) return null;

    const formErrors = {};
    for (let item of error.details) {
      formErrors[item.path[0]] = item.message;
    }
    return formErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate the entire form
    const formErrors = validateForm();
    
    // If there are validation errors, set them and stop submission
    if (formErrors) {
      setErrors(formErrors);
      return;
    }

    setSubmitting(true);
    try {
      await submitLead({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        city: formData.country,
        message: formData.message,
      }, `Blog: ${blogData.title}`);
      // Reset form after successful submission
      setFormData({
        name: '',
        phone: '',
        email: '',
        country: '',
        message: ''
      });
      setErrors({}); // Clear any existing errors

      toast.success('Query submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(`Couldn't send your query. Please call us on ${CONTACT_PHONE}.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <SiteHeader />
        <main className="articlePage" aria-busy="true">
          <div className="articleHero">
            <div className="articleNarrow">
              <div className="skeleton skeleton--line" style={{ width: 180 }} />
              <div className="skeleton skeleton--title" />
              <div className="skeleton skeleton--title" style={{ width: '70%' }} />
              <div className="skeleton skeleton--line" style={{ width: 260 }} />
            </div>
          </div>
          <div className="articleNarrow">
            <div className="skeleton skeleton--cover" />
          </div>
        </main>
      </>
    );
  }

  if (!blogData) {
    return (
      <>
        <Seo title="Article Not Found" path={`/blog/${slug}`} noindex />
        <SiteHeader />
        <main className="articlePage articleNotFound">
          <h1>Article not found</h1>
          <p>This article may have been moved or removed.</p>
          <Link to="/blogs" className="articleBtn">Browse all articles <FaArrowRight aria-hidden="true" /></Link>
        </main>
        <FooterArea />
      </>
    );
  }

  // Demote any <h1> inside the post body to <h2> so the page keeps a single h1 (the title).
  // Quill's empty <p><br></p> spacers are dropped; spacing comes from the prose styles.
  const contentHtml = (blogData.content || '')
    .replace(/<(\/?)h1(\s|>)/gi, '<$1h2$2')
    .replace(/<p>(\s|<br\s*\/?>)*<\/p>/gi, '');

  // Derive SEO metadata from the blog content.
  const plainText = toPlainText(blogData.content);
  const excerpt = plainText.length > 155 ? `${plainText.slice(0, 152)}...` : plainText;
  // Editable SEO overrides from the admin, falling back to sensible defaults.
  const seoTitle = (blogData.meta_title || '').trim() || blogData.title;
  const seoDescription = (blogData.meta_description || '').trim() || excerpt || `${blogData.title} — legal insights from Legal Corridor.`;
  const faqs = Array.isArray(blogData.faqs) ? blogData.faqs.filter((f) => f && f.question && f.answer) : [];
  // Comments are read-only archives from the old backend (posting needs a server).
  const comments = Array.isArray(blogData.comments) ? blogData.comments : [];
  const wordCount = plainText.split(/\s+/).filter(Boolean).length;
  const readMins = readingTimeMinutes(blogData.content);
  const toIso = (d) => {
    const t = new Date(d);
    return isNaN(t.getTime()) ? undefined : t.toISOString();
  };
  const publishedIso = toIso(blogData.creation_date);
  const canonicalPath = blogPath(blogData);
  const shareUrl = absoluteUrl(canonicalPath);
  // Lightweight keywords derived from the title (stopwords removed).
  const stop = new Set(['the', 'and', 'for', 'a', 'an', 'of', 'to', 'in', 'on', 'under', 'your', 'with', 'how', 'is', 'as', 'at', 'by']);
  const keywords = Array.from(
    new Set((blogData.title || '').toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter((w) => w.length > 3 && !stop.has(w)))
  ).slice(0, 8);
  const hashtags = keywords.slice(0, 3).map((w) => w.replace(/[^a-z0-9]/g, '')).filter(Boolean);
  // Prefer real tags assigned in the admin panel; fall back to derived keywords.
  const realTags = Array.isArray(blogData.tags) ? blogData.tags.filter(Boolean) : [];
  const hasRealTags = realTags.length > 0;
  const displayTags = hasRealTags ? realTags : keywords;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blogData.title,
    description: excerpt,
    image: blogData.banner_url ? [absoluteAsset(blogData.banner_url)] : undefined,
    author: { '@type': 'Person', name: blogData.creator_name || SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo.png` },
    },
    datePublished: publishedIso,
    dateModified: toIso(blogData.updation_date) || publishedIso,
    mainEntityOfPage: { '@type': 'WebPage', '@id': shareUrl },
    url: shareUrl,
    inLanguage: 'en-IN',
    articleSection: 'Legal Insights',
    wordCount: wordCount || undefined,
    keywords: displayTags.length ? displayTags.join(', ') : undefined,
    isPartOf: { '@type': 'Blog', '@id': `${SITE_URL}/blogs`, name: 'Legal Corridor Blog' },
  };

  const shareLinks = [
    { label: 'Share on WhatsApp', Icon: FaWhatsapp, href: `https://wa.me/?text=${encodeURIComponent(blogData.title + ' ' + shareUrl)}` },
    { label: 'Share on Facebook', Icon: FaFacebookF, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: 'Share on LinkedIn', Icon: FaLinkedinIn, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { label: 'Share on X', Icon: FaXTwitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(blogData.title)}${hashtags.length ? `&hashtags=${hashtags.join(',')}` : ''}` },
  ];

  const copyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl).then(() => toast.success('Link copied!')).catch(() => {});
    }
  };

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={canonicalPath}
        image={absoluteAsset(blogData.banner_url)}
        type="article"
        article={{ publishedTime: publishedIso, author: blogData.creator_name }}
        schema={[
          articleSchema,
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Legal Insights', path: '/blogs' },
            { name: blogData.title, path: canonicalPath },
          ]),
          ...(faqs.length ? [faqSchema(faqs)] : []),
        ]}
      />
      <SiteHeader />
      <ReadingProgress />
      <main className="articlePage">
        <header className="articleHero">
          <div className="articleNarrow">
            <nav className="articleCrumbs" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <Link to="/blogs">Legal Insights</Link>
            </nav>
            {hasRealTags && (
              <div className="articleKicker">
                {realTags.slice(0, 2).map((k) => (
                  <Link key={k} to={tagPath(k)}>{k}</Link>
                ))}
              </div>
            )}
            <h1 className="articleTitle">{blogData.title}</h1>
            <div className="articleMeta">
              <span className="articleAuthor">
                <span className="articleAvatar" aria-hidden="true">{(blogData.creator_name || 'L').replace(/^(adv\.?|advocate)\s+/i, '').charAt(0)}</span>
                {blogData.creator_name || 'Legal Corridor'}
              </span>
              {publishedIso && (
                <span><FaRegCalendar aria-hidden="true" /> <time dateTime={publishedIso}>{formatDate(blogData.creation_date)}</time></span>
              )}
              <span><FaRegClock aria-hidden="true" /> {readMins} min read</span>
            </div>
          </div>
        </header>

        <div className="articleLayout">
          <article className="articleMain">
            {blogData.banner_url && (
              <figure className="articleCover">
                <img src={blogData.banner_url} alt={blogData.title} width="1200" height="675" fetchpriority="high" />
              </figure>
            )}

            <div className="prose" dangerouslySetInnerHTML={{ __html: contentHtml }} />

            {displayTags.length > 0 && (
              <div className="articleTags" aria-label="Topics">
                {displayTags.map((k) =>
                  hasRealTags ? (
                    <Link key={k} to={tagPath(k)} className="tagChip">#{k}</Link>
                  ) : (
                    <span key={k} className="tagChip">#{k}</span>
                  )
                )}
              </div>
            )}

            <div className="articleShare" aria-label="Share this article">
              <span className="articleShareLabel">Share this article</span>
              <div className="articleShareBtns">
                {shareLinks.map(({ label, Icon, href }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer nofollow" aria-label={label} className="shareBtn">
                    <Icon />
                  </a>
                ))}
                <button type="button" className="shareBtn" aria-label="Copy link" onClick={copyLink}>
                  <FaLink />
                </button>
              </div>
            </div>
          </article>

          <aside className="articleAside" aria-label="Book a consultation">
            <div className="consultCard">
              <p className="consultEyebrow">Free consultation</p>
              <h2>Need help with a similar matter?</h2>
              <p className="consultLead">Tell us briefly about your case. An advocate will call you back within 24 hours.</p>
              <form onSubmit={handleSubmit} noValidate>
                <label className="field">
                  <span className="visuallyHidden">Full name</span>
                  <input type="text" name="name" placeholder="Full name *" autoComplete="name" value={formData.name} onChange={handleInputChange} aria-invalid={Boolean(errors.name)} />
                  {errors.name && <span className="fieldError">{errors.name}</span>}
                </label>
                <label className="field">
                  <span className="visuallyHidden">Mobile number</span>
                  <input type="tel" name="phone" placeholder="Mobile number *" autoComplete="tel" inputMode="tel" value={formData.phone} onChange={handleInputChange} aria-invalid={Boolean(errors.phone)} />
                  {errors.phone && <span className="fieldError">{errors.phone}</span>}
                </label>
                <label className="field">
                  <span className="visuallyHidden">Email</span>
                  <input type="email" name="email" placeholder="Email *" autoComplete="email" value={formData.email} onChange={handleInputChange} aria-invalid={Boolean(errors.email)} />
                  {errors.email && <span className="fieldError">{errors.email}</span>}
                </label>
                <label className="field">
                  <span className="visuallyHidden">City</span>
                  <input type="text" name="country" placeholder="City" autoComplete="address-level2" value={formData.country} onChange={handleInputChange} />
                </label>
                <label className="field">
                  <span className="visuallyHidden">Your legal issue</span>
                  <textarea name="message" rows={4} placeholder="Briefly describe your legal issue *" value={formData.message} onChange={handleInputChange} aria-invalid={Boolean(errors.message)} />
                  {errors.message && <span className="fieldError">{errors.message}</span>}
                </label>
                <button type="submit" className="articleBtn articleBtn--block" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Request a callback'}
                </button>
              </form>
              <a href="tel:+919555690946" className="consultCall"><FaPhone aria-hidden="true" /> Or call {CONTACT_PHONE}</a>
            </div>
          </aside>
        </div>

        {faqs.length > 0 && (
          <FaqSection title="Frequently Asked Questions" subTitle="About This Topic" faqs={faqs} />
        )}

        {(comments.length > 0 || relatedBlogs.length > 0) && (
          <div className="articleExtras">
            {comments.length > 0 && (
              <section className="commentsSection" aria-label="Comments">
                <h2>{`Comments (${comments.length})`}</h2>
                <div className="commentList">
                  {comments.map((cm, i) => (
                    <div className="commentItem" key={i}>
                      <div className="commentAvatar" aria-hidden="true">
                        {(cm.name || '?').trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="commentBody">
                        <div className="commentHead">
                          <span className="commentName">{cm.name}</span>
                          {cm.created_at && <span className="commentDate">{formatDate(cm.created_at)}</span>}
                        </div>
                        <p className="commentText">{cm.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {relatedBlogs.length > 0 && (
              <section className="relatedArticles" aria-label="Related articles">
                <div className="relatedHead">
                  <h2>Keep reading</h2>
                  <Link to="/blogs">All articles <FaArrowRight aria-hidden="true" /></Link>
                </div>
                <div className="relatedGrid">
                  {relatedBlogs.map((b) => (
                    <Link key={b.slug} to={blogPath(b)} className="relatedCard">
                      <div className="relatedThumb">
                        <img src={b.banner_url} alt="" loading="lazy" decoding="async" />
                      </div>
                      <div className="relatedBody">
                        <span className="relatedDate">{formatDate(b.creation_date)}</span>
                        <h3>{b.title}</h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      <FooterArea />
    </>
  );
};

// Thin gold bar at the top of the viewport showing how far through the article you are.
const ReadingProgress = () => {
  const bar = useRef(null);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (bar.current) bar.current.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return <div className="readingProgress" ref={bar} aria-hidden="true" />;
};

export default BlogPage;